import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import axiosInstance from "../axiosInstance";

const cardSx = {
  height: "100%",
  p: 2.5,
  borderRadius: "16px",
  border: "1px solid rgba(255, 132, 76, 0.10)",
  background: "linear-gradient(180deg, rgba(255, 247, 241, 0.96), rgba(255,255,255,0.92))",
};

const fieldSx = {
  mb: 1.5,
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fffaf7",
    "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" },
    "&:hover fieldset": { borderColor: "#f97316" },
    "&.Mui-focused fieldset": { borderColor: "#f97316" },
  },
};

const actionDetails = {
  tenant: {
    title: "Invalidate tenant cache?",
    description: "Every V2 cache entry tagged for this tenant will be invalidated.",
    buttonLabel: "Invalidate Tenant Cache",
  },
  greetings: {
    title: "Invalidate all greetings?",
    description: "Every V2 greeting cache entry tagged for this tenant will be invalidated.",
    buttonLabel: "Invalidate All Greetings",
  },
  "product-exists": {
    title: "Invalidate all Exists cache?",
    description: "Every V2 Exists API cache entry tagged for this tenant will be invalidated.",
    buttonLabel: "Invalidate All Exists cache",
  },
  product: {
    title: "Invalidate product cache?",
    description: "V2 cache entries tagged for this tenant and product ID will be invalidated.",
    buttonLabel: "Invalidate Product Cache",
  },
  url: {
    title: "Invalidate URL cache?",
    description: "V2 cache entries tagged for this tenant and URL will be invalidated.",
    buttonLabel: "Invalidate URL Cache",
  },
};

const cacheNames = ["Chat", "Products", "TenantConfig", "Unknown"];
const cacheKinds = [
  "Unknown",
  "Product",
  "Greeting",
  "Operation",
  "UrlExists",
  "ProductIdExists",
  "TenantUrl",
  "QuickView",
];

const kindChipStyles = {
  Product: { color: "#c2410c", backgroundColor: "#fff7ed", borderColor: "#fdba74" },
  Greeting: { color: "#7e22ce", backgroundColor: "#faf5ff", borderColor: "#d8b4fe" },
  Operation: { color: "#1d4ed8", backgroundColor: "#eff6ff", borderColor: "#93c5fd" },
  UrlExists: { color: "#0e7490", backgroundColor: "#ecfeff", borderColor: "#67e8f9" },
  ProductIdExists: { color: "#047857", backgroundColor: "#ecfdf5", borderColor: "#6ee7b7" },
  TenantUrl: { color: "#a16207", backgroundColor: "#fefce8", borderColor: "#fde047" },
  QuickView: { color: "#6d28d9", backgroundColor: "#f5f3ff", borderColor: "#c4b5fd" },
  Unknown: { color: "#4b5563", backgroundColor: "#f9fafb", borderColor: "#d1d5db" },
};

const cacheNameChipStyles = {
  Chat: { color: "#4338ca", backgroundColor: "#eef2ff", borderColor: "#a5b4fc" },
  Products: { color: "#c2410c", backgroundColor: "#fff7ed", borderColor: "#fdba74" },
  TenantConfig: { color: "#15803d", backgroundColor: "#f0fdf4", borderColor: "#86efac" },
  Unknown: { color: "#4b5563", backgroundColor: "#f9fafb", borderColor: "#d1d5db" },
};

const tagChipStyles = [
  { match: "tenant", color: "#6d28d9", backgroundColor: "#f5f3ff", borderColor: "#c4b5fd" },
  { match: "product", color: "#c2410c", backgroundColor: "#fff7ed", borderColor: "#fdba74" },
  { match: "greeting", color: "#be185d", backgroundColor: "#fdf2f8", borderColor: "#f9a8d4" },
  { match: "url", color: "#0369a1", backgroundColor: "#f0f9ff", borderColor: "#7dd3fc" },
  { match: "operation", color: "#1d4ed8", backgroundColor: "#eff6ff", borderColor: "#93c5fd" },
];

const defaultChipStyle = {
  color: "#4b5563",
  backgroundColor: "#f9fafb",
  borderColor: "#d1d5db",
};

const defaultInspectionFilters = {
  cacheName: "",
  kind: "",
  keyContains: "",
  tags: "",
  tagMatch: "All",
  includeValue: false,
  pageSize: 100,
};

const getErrorMessage = (error, fallback = "Failed to invalidate cache.") => {
  const responseData = error.response?.data;

  if (typeof responseData === "string" && responseData.trim()) return responseData;
  if (responseData?.message) return responseData.message;
  if (responseData?.title) return responseData.title;
  return fallback;
};

const isAbsoluteUrlWithHost = (value) => {
  try {
    return Boolean(new URL(value).hostname);
  } catch {
    return false;
  }
};

function CacheActionCard({ icon: Icon, title, description, children }) {
  return (
    <Paper elevation={0} sx={cardSx}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Icon sx={{ color: "#f97316", fontSize: 21 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#202531" }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
        {description}
      </Typography>
      {children}
    </Paper>
  );
}

const normalizeKind = (kind) => {
  if (typeof kind === "number") return cacheKinds[kind] || `Kind ${kind}`;
  if (typeof kind === "string" && /^\d+$/.test(kind)) {
    return cacheKinds[Number(kind)] || `Kind ${kind}`;
  }
  return kind || "Unknown";
};

const getKindChipStyle = (kind) =>
  kindChipStyles[normalizeKind(kind)] || kindChipStyles.Unknown;

const getCacheNameChipStyle = (cacheName) =>
  cacheNameChipStyles[cacheName] || cacheNameChipStyles.Unknown;

const kindIcons = {
  Product: Inventory2OutlinedIcon,
  Greeting: ChatOutlinedIcon,
  Operation: BoltOutlinedIcon,
  UrlExists: LinkOutlinedIcon,
  ProductIdExists: Inventory2OutlinedIcon,
  TenantUrl: StoreOutlinedIcon,
  QuickView: ViewModuleOutlinedIcon,
  Unknown: ManageSearchOutlinedIcon,
};

const getTagChipStyle = (tag) => {
  const normalizedTag = tag.toLowerCase();
  return tagChipStyles.find(({ match }) => normalizedTag.includes(match)) || defaultChipStyle;
};

const parseTags = (value) =>
  value
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag, index, tags) => tag && tags.indexOf(tag) === index);

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatTtl = (seconds) => {
  if (seconds === null || seconds === undefined) return "No expiry";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

const formatValue = (value) => {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const copyToClipboard = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textArea);

  if (!copied) throw new Error("Clipboard copy failed.");
};

function CacheEntryLog({ entry, includeValue }) {
  const tags = Array.isArray(entry.tags) ? entry.tags : [];
  const kind = normalizeKind(entry.kind);
  const cacheName = entry.cacheName || "Unknown";
  const KindIcon = kindIcons[kind] || kindIcons.Unknown;
  const kindStyle = getKindChipStyle(kind);

  const handleCopyTag = async (tag) => {
    try {
      await copyToClipboard(tag);
    } catch (error) {
      console.error("Failed to copy cache tag:", error);
    }
  };

  return (
    <Paper
      component="article"
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: "14px",
        borderColor: "rgba(249, 115, 22, 0.18)",
        backgroundColor: "#fffdfb",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <Box
          aria-hidden="true"
          sx={{
            order: 2,
            width: 38,
            height: 38,
            flex: "0 0 38px",
            display: "grid",
            placeItems: "center",
            borderRadius: "11px",
            color: kindStyle.color,
            backgroundColor: kindStyle.backgroundColor,
            border: `1px solid ${kindStyle.borderColor}`,
          }}
        >
          <KindIcon sx={{ fontSize: 21 }} />
        </Box>

        <Box sx={{ order: 1, flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, mb: 0.75 }}>
            <Chip
              label={kind}
              size="small"
              variant="outlined"
              sx={{ ...kindStyle, fontWeight: 700 }}
            />
            <Chip
              label={`Cache · ${cacheName}`}
              size="small"
              variant="outlined"
              sx={{ ...getCacheNameChipStyle(cacheName), fontWeight: 700 }}
            />
            {entry.productId && (
              <Chip
                label={`Product · ${entry.productId}`}
                size="small"
                variant="outlined"
                sx={{ ...kindChipStyles.Product, fontWeight: 700 }}
              />
            )}
          </Box>

          {entry.key && entry.key !== entry.logicalKey && (
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                flexWrap: "wrap",
                columnGap: 1,
                mt: 0.25,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#6b7280", fontSize: 10, fontWeight: 800, textTransform: "uppercase" }}
              >
                Physical key
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#6b7280", fontFamily: "monospace", wordBreak: "break-all" }}
              >
                {entry.key}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          flexWrap: "wrap",
          columnGap: 3,
          rowGap: 0.5,
          mt: 1.25,
          pt: 1.1,
          borderTop: "1px solid #f1f5f9",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
          <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 700 }}>
            Created
          </Typography>
          <Typography variant="body2" sx={{ color: "#202531" }}>
            {formatDate(entry.createdAt)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
          <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 700 }}>
            TTL
          </Typography>
          <Typography variant="body2" sx={{ color: "#202531" }}>
            {formatTtl(entry.timeToLiveSeconds)}
          </Typography>
        </Box>
        {tags.length > 0 && (
          <Typography
            variant="caption"
            sx={{
              ml: "auto",
              color: "#6b7280",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Tags · {tags.length}
          </Typography>
        )}
      </Box>

      {tags.length > 0 && (
        <Box sx={{ mt: 1.1 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                aria-label={`Copy tag ${tag}`}
                icon={<ContentCopyOutlinedIcon />}
                size="small"
                variant="outlined"
                clickable
                onClick={() => handleCopyTag(tag)}
                sx={{
                  ...getTagChipStyle(tag),
                  maxWidth: "100%",
                  fontWeight: 650,
                  "& .MuiChip-icon": { color: "inherit", fontSize: 14 },
                  "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

              {includeValue && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography
            variant="caption"
            sx={{
              color: "#4338ca",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Cached value · JSON
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              mt: 0.75,
              p: 1.5,
              maxHeight: 280,
              overflow: "auto",
              borderRadius: "10px",
              backgroundColor: "#202531",
              color: "#f9fafb",
              fontSize: 12,
              lineHeight: 1.55,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {entry.value === null || entry.value === undefined
              ? "No cached value returned."
              : formatValue(entry.value)}
          </Box>
        </>
      )}
    </Paper>
  );
}

const CacheV2Section = ({ tenantId, primaryButtonSx }) => {
  const [productId, setProductId] = useState("");
  const [url, setUrl] = useState("");
  const [validationMessage, setValidationMessage] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [invalidatingType, setInvalidatingType] = useState(null);
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const [inspectionFilters, setInspectionFilters] = useState(defaultInspectionFilters);
  const [appliedInspectionFilters, setAppliedInspectionFilters] = useState(defaultInspectionFilters);
  const [inspectionData, setInspectionData] = useState(null);
  const [inspectionError, setInspectionError] = useState(null);
  const [inspectionFeedback, setInspectionFeedback] = useState(null);
  const [loadingInspection, setLoadingInspection] = useState(false);
  const [loadingMoreInspection, setLoadingMoreInspection] = useState(false);
  const [tagInvalidationOpen, setTagInvalidationOpen] = useState(false);
  const [invalidatingTags, setInvalidatingTags] = useState(false);

  useEffect(() => {
    setProductId("");
    setUrl("");
    setValidationMessage(null);
    setFeedback(null);
    setPendingAction(null);
    setInvalidatingType(null);
    setInspectionOpen(false);
    setInspectionFilters(defaultInspectionFilters);
    setAppliedInspectionFilters(defaultInspectionFilters);
    setInspectionData(null);
    setInspectionError(null);
    setInspectionFeedback(null);
    setLoadingInspection(false);
    setLoadingMoreInspection(false);
    setTagInvalidationOpen(false);
    setInvalidatingTags(false);
  }, [tenantId]);

  const fetchCacheEntries = async ({
    filters = inspectionFilters,
    cursor = null,
    append = false,
  } = {}) => {
    if (!tenantId) return;

    const setLoading = append ? setLoadingMoreInspection : setLoadingInspection;
    setLoading(true);
    setInspectionError(null);

    const tags = parseTags(filters.tags);
    const params = {
      IncludeValue: filters.includeValue,
      PageSize: filters.pageSize,
      TagMatch: filters.tagMatch,
    };

    if (filters.cacheName) params.CacheName = filters.cacheName;
    if (filters.kind) params.Kind = filters.kind;
    if (filters.keyContains.trim()) params.KeyContains = filters.keyContains.trim();
    if (tags.length > 0) params.tag = tags;
    if (cursor) params.Cursor = cursor;

    try {
      const response = await axiosInstance.get(
        `/cache/tenants/${encodeURIComponent(tenantId)}/entries`,
        {
          params,
          paramsSerializer: { indexes: null },
          headers: { Tenantid: tenantId },
        }
      );
      const result = response.data || {};
      setInspectionData((current) => ({
        ...result,
        items: append
          ? [...(current?.items || []), ...(result.items || [])]
          : (result.items || []),
      }));
      setAppliedInspectionFilters(filters);
    } catch (error) {
      console.error("Error loading V2 cache entries:", error);
      setInspectionError(getErrorMessage(error, "Failed to load V2 cache entries."));
    } finally {
      setLoading(false);
    }
  };

  const openInspection = () => {
    if (!tenantId) return;
    setInspectionFeedback(null);
    setInspectionOpen(true);
    fetchCacheEntries();
  };

  const applyInspectionFilters = () => {
    setInspectionFeedback(null);
    fetchCacheEntries({ filters: inspectionFilters });
  };

  const confirmTagInvalidation = async () => {
    const tags = parseTags(appliedInspectionFilters.tags);
    if (!tenantId || tags.length === 0) return;

    setInvalidatingTags(true);
    setInspectionError(null);
    setInspectionFeedback(null);

    try {
      const response = await axiosInstance.delete("/cache/invalidate/tags", {
        data: {
          TenantId: tenantId,
          Tags: tags,
        },
        headers: { Tenantid: tenantId },
      });
      setTagInvalidationOpen(false);
      await fetchCacheEntries({ filters: appliedInspectionFilters });
      setInspectionFeedback({
        type: "success",
        text: response.data?.message || "Cache tags invalidated successfully.",
      });
    } catch (error) {
      console.error("Error invalidating cache tags:", error);
      setTagInvalidationOpen(false);
      setInspectionError(getErrorMessage(error, "Failed to invalidate cache tags."));
    } finally {
      setInvalidatingTags(false);
    }
  };

  const prepareInvalidation = (type) => {
    setValidationMessage(null);
    setFeedback(null);

    if (!tenantId) {
      setValidationMessage("Select a tenant before invalidating cache.");
      return;
    }

    if (type === "product" && !productId.trim()) {
      setValidationMessage("Product ID is required.");
      return;
    }

    if (type === "url" && !url.trim()) {
      setValidationMessage("URL is required.");
      return;
    }

    if (type === "url" && !isAbsoluteUrlWithHost(url.trim())) {
      setValidationMessage("Enter a valid absolute URL, including the protocol.");
      return;
    }

    const data = { TenantId: tenantId };
    if (type === "product") data.ProductId = productId.trim();
    if (type === "url") data.Url = url.trim();

    setPendingAction({ type, data });
  };

  const confirmInvalidation = async () => {
    if (!pendingAction || !tenantId) return;

    const { type, data } = pendingAction;
    setPendingAction(null);
    setInvalidatingType(type);
    setFeedback(null);

    try {
      const response = await axiosInstance.delete(`/cache/invalidate/${type}`, {
        data,
        headers: { Tenantid: tenantId },
      });
      const responseData = response.data || {};
      setFeedback({
        type: "success",
        text: responseData.message || "Cache invalidated successfully.",
        tag: responseData.tag,
      });
      if (type === "product") setProductId("");
      if (type === "url") setUrl("");
    } catch (error) {
      console.error(`Error invalidating ${type} cache:`, error);
      setFeedback({ type: "error", text: getErrorMessage(error) });
    } finally {
      setInvalidatingType(null);
    }
  };

  const buttonContent = (type, label) =>
    invalidatingType === type ? (
      <>
        <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
        Invalidating...
      </>
    ) : (
      label
    );

  const isBusy = Boolean(invalidatingType);
  const pendingDetails = pendingAction ? actionDetails[pendingAction.type] : null;
  const appliedTags = parseTags(appliedInspectionFilters.tags);
  const canInvalidateAppliedTags =
    appliedTags.length > 0
    && Boolean(inspectionData)
    && (inspectionData.totalCount ?? 0) > 0;

  return (
    <>
      {(validationMessage || feedback) && (
        <Box
          role="alert"
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: "12px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: feedback?.type === "success" ? "rgba(34, 197, 94, 0.08)" : "rgba(239, 68, 68, 0.08)",
            border: `1px solid ${feedback?.type === "success" ? "rgba(34, 197, 94, 0.3)" : "rgba(239, 68, 68, 0.3)"}`,
          }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{ color: feedback?.type === "success" ? "#16a34a" : "#dc2626", fontWeight: 600 }}
            >
              {validationMessage || feedback?.text}
            </Typography>
            {feedback?.tag && (
              <Typography
                variant="caption"
                component="div"
                sx={{ color: "#15803d", mt: 0.5, fontFamily: "monospace", wordBreak: "break-all" }}
              >
                Tag: {feedback.tag}
              </Typography>
            )}
          </Box>
          <IconButton
            aria-label="Dismiss message"
            size="small"
            onClick={() => {
              setValidationMessage(null);
              setFeedback(null);
            }}
            sx={{ color: feedback?.type === "success" ? "#16a34a" : "#dc2626" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <CacheActionCard
            icon={ManageSearchOutlinedIcon}
            title="Cache Entries"
            description="Inspect the live V2 cache entries associated with this tenant."
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<ManageSearchOutlinedIcon />}
              onClick={openInspection}
              disabled={!tenantId || isBusy}
              sx={primaryButtonSx}
            >
              Show Cache Entries
            </Button>
          </CacheActionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <CacheActionCard
            icon={StoreOutlinedIcon}
            title="Entire Tenant"
            description="Invalidate all V2 cache entries associated with this tenant."
          >
            <Typography
              variant="body2"
              sx={{
                mb: 1.5,
                px: 1.5,
                py: 1.25,
                borderRadius: "10px",
                backgroundColor: "#fffaf7",
                border: "1px solid rgba(255, 132, 76, 0.20)",
                color: "#202531",
                fontWeight: 700,
                wordBreak: "break-all",
              }}
            >
              {(tenantId || "No tenant selected").toUpperCase()}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={invalidatingType === "tenant" ? undefined : <CachedOutlinedIcon />}
              onClick={() => prepareInvalidation("tenant")}
              disabled={!tenantId || isBusy}
              sx={primaryButtonSx}
            >
              {buttonContent("tenant", "Invalidate Tenant Cache")}
            </Button>
          </CacheActionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <CacheActionCard
            icon={ChatOutlinedIcon}
            title="All Greetings"
            description="Invalidate all V2 greeting cache entries associated with this tenant."
          >
            <Button
              variant="contained"
              size="small"
              onClick={() => prepareInvalidation("greetings")}
              disabled={!tenantId || isBusy}
              sx={primaryButtonSx}
            >
              {buttonContent("greetings", "Invalidate All Greetings")}
            </Button>
          </CacheActionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <CacheActionCard
            icon={ViewModuleOutlinedIcon}
            title="Exists API"
            description="Invalidate all V2 Exists API cache entries associated with this tenant."
          >
            <Button
              variant="contained"
              size="small"
              onClick={() => prepareInvalidation("product-exists")}
              disabled={!tenantId || isBusy}
              sx={primaryButtonSx}
            >
              {buttonContent("product-exists", "Invalidate All Exists cache")}
            </Button>
          </CacheActionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <CacheActionCard
            icon={Inventory2OutlinedIcon}
            title="Product ID"
            description="Invalidate V2 cache entries associated with a specific product ID."
          >
            <TextField
              label="Product ID"
              placeholder="Enter Product ID"
              value={productId}
              onChange={(event) => setProductId(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && productId.trim() && !isBusy) prepareInvalidation("product");
              }}
              size="small"
              fullWidth
              disabled={isBusy}
              sx={fieldSx}
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => prepareInvalidation("product")}
              disabled={!tenantId || !productId.trim() || isBusy}
              sx={primaryButtonSx}
            >
              {buttonContent("product", "Invalidate Product Cache")}
            </Button>
          </CacheActionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <CacheActionCard
            icon={LinkOutlinedIcon}
            title="URL"
            description="Invalidate V2 cache entries associated with a specific absolute URL."
          >
            <TextField
              label="URL"
              placeholder="https://example.com/products/item"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && url.trim() && !isBusy) prepareInvalidation("url");
              }}
              size="small"
              fullWidth
              disabled={isBusy}
              sx={fieldSx}
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => prepareInvalidation("url")}
              disabled={!tenantId || !url.trim() || isBusy}
              sx={primaryButtonSx}
            >
              {buttonContent("url", "Invalidate URL Cache")}
            </Button>
          </CacheActionCard>
        </Grid>
      </Grid>

      <Dialog
        open={Boolean(pendingAction)}
        onClose={() => setPendingAction(null)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            border: "1px solid rgba(249, 115, 22, 0.18)",
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#202531" }}>
          {pendingDetails?.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            {pendingDetails?.description}
          </Typography>
          {pendingAction?.type !== "tenant" && (
            <Typography
              variant="body2"
              sx={{ color: "#202531", fontWeight: 600, mt: 1.5, wordBreak: "break-all" }}
            >
              {pendingAction?.type === "product" ? pendingAction.data.ProductId : pendingAction?.data.Url}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendingAction(null)} sx={{ color: "#6b7280" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmInvalidation} sx={primaryButtonSx} autoFocus>
            {pendingDetails?.buttonLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={inspectionOpen}
        onClose={() => setInspectionOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            border: "1px solid rgba(249, 115, 22, 0.18)",
            minHeight: { xs: "90vh", md: "76vh" },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            pb: 1,
          }}
        >
          <Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: "#202531" }}>
              Cache V2 Entries
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
              Live cache inspection for {tenantId}
            </Typography>
          </Box>
          <IconButton aria-label="Close cache entries" onClick={() => setInspectionOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: "#fffaf7" }}>
          <Paper
            component="form"
            onSubmit={(event) => {
              event.preventDefault();
              applyInspectionFilters();
            }}
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              border: "1px solid rgba(249, 115, 22, 0.16)",
              borderRadius: "14px",
              backgroundColor: "#fff",
            }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="cache-name-filter-label" shrink>Cache name</InputLabel>
                  <Select
                    labelId="cache-name-filter-label"
                    label="Cache name"
                    value={inspectionFilters.cacheName}
                    displayEmpty
                    renderValue={(selected) => selected || "All caches"}
                    onChange={(event) =>
                      setInspectionFilters((current) => ({ ...current, cacheName: event.target.value }))
                    }
                  >
                    <MenuItem value="">All caches</MenuItem>
                    {cacheNames.map((cacheName) => (
                      <MenuItem key={cacheName} value={cacheName}>{cacheName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="cache-kind-filter-label" shrink>Entry kind</InputLabel>
                  <Select
                    labelId="cache-kind-filter-label"
                    label="Entry kind"
                    value={inspectionFilters.kind}
                    displayEmpty
                    renderValue={(selected) => selected || "All kinds"}
                    onChange={(event) =>
                      setInspectionFilters((current) => ({ ...current, kind: event.target.value }))
                    }
                  >
                    <MenuItem value="">All kinds</MenuItem>
                    {cacheKinds.map((kind) => (
                      <MenuItem key={kind} value={kind}>{kind}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Key contains"
                  value={inspectionFilters.keyContains}
                  onChange={(event) =>
                    setInspectionFilters((current) => ({ ...current, keyContains: event.target.value }))
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Tags"
                  placeholder="tag-one, tag-two"
                  value={inspectionFilters.tags}
                  onChange={(event) =>
                    setInspectionFilters((current) => ({ ...current, tags: event.target.value }))
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="tag-match-filter-label">Tag match</InputLabel>
                  <Select
                    labelId="tag-match-filter-label"
                    label="Tag match"
                    value={inspectionFilters.tagMatch}
                    onChange={(event) =>
                      setInspectionFilters((current) => ({ ...current, tagMatch: event.target.value }))
                    }
                  >
                    <MenuItem value="All">Match all</MenuItem>
                    <MenuItem value="Any">Match any</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="page-size-filter-label">Page size</InputLabel>
                  <Select
                    labelId="page-size-filter-label"
                    label="Page size"
                    value={inspectionFilters.pageSize}
                    onChange={(event) =>
                      setInspectionFilters((current) => ({ ...current, pageSize: event.target.value }))
                    }
                  >
                    {[25, 50, 100, 200].map((pageSize) => (
                      <MenuItem key={pageSize} value={pageSize}>{pageSize}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={inspectionFilters.includeValue}
                      onChange={(event) =>
                        setInspectionFilters((current) => ({
                          ...current,
                          includeValue: event.target.checked,
                        }))
                      }
                      color="warning"
                    />
                  }
                  label="Include cached values"
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ display: "flex", justifyContent: { md: "flex-end" }, gap: 1 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<RefreshOutlinedIcon />}
                    onClick={() => fetchCacheEntries({ filters: appliedInspectionFilters })}
                    disabled={loadingInspection || loadingMoreInspection}
                    sx={{ borderColor: "#f97316", color: "#f97316" }}
                  >
                    Refresh
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SearchOutlinedIcon />}
                    disabled={loadingInspection || loadingMoreInspection}
                    sx={primaryButtonSx}
                  >
                    Apply filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {(inspectionError || inspectionFeedback) && (
            <Box
              role="alert"
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: "12px",
                color: inspectionError ? "#dc2626" : "#15803d",
                backgroundColor: inspectionError
                  ? "rgba(239, 68, 68, 0.08)"
                  : "rgba(34, 197, 94, 0.08)",
                border: `1px solid ${
                  inspectionError
                    ? "rgba(239, 68, 68, 0.3)"
                    : "rgba(34, 197, 94, 0.3)"
                }`,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {inspectionError || inspectionFeedback?.text}
              </Typography>
            </Box>
          )}

          {loadingInspection ? (
            <Box sx={{ minHeight: 260, display: "grid", placeItems: "center" }}>
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress size={30} sx={{ color: "#f97316" }} />
                <Typography variant="body2" sx={{ color: "#6b7280", mt: 1 }}>
                  Loading cache entries...
                </Typography>
              </Box>
            </Box>
          ) : inspectionData && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2 }}>
                <Chip label={`${inspectionData.totalCount ?? 0} total`} sx={{ fontWeight: 700 }} />
                {Object.entries(inspectionData.countsByKind || {}).map(([kind, count]) => (
                  <Chip
                    key={kind}
                    label={`${normalizeKind(kind)}: ${count}`}
                    size="small"
                    variant="outlined"
                    sx={{ ...getKindChipStyle(kind), fontWeight: 700 }}
                  />
                ))}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    ml: { sm: "auto" },
                  }}
                >
                  {canInvalidateAppliedTags && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteSweepOutlinedIcon />}
                      onClick={() => setTagInvalidationOpen(true)}
                      disabled={invalidatingTags || loadingInspection || loadingMoreInspection}
                      sx={{ fontWeight: 700 }}
                    >
                      Invalidate by tag
                    </Button>
                  )}
                  <Typography variant="caption" sx={{ color: "#6b7280" }}>
                    Showing {(inspectionData.items || []).length}
                  </Typography>
                </Box>
              </Box>

              {(inspectionData.items || []).length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 4, borderRadius: "14px", textAlign: "center", borderStyle: "dashed" }}
                >
                  <ManageSearchOutlinedIcon sx={{ fontSize: 36, color: "#9ca3af", mb: 1 }} />
                  <Typography variant="subtitle2" sx={{ color: "#202531", fontWeight: 700 }}>
                    No cache entries found
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                    Try changing the filters or refresh the cache inspection.
                  </Typography>
                </Paper>
              ) : (
                <Box sx={{ display: "grid", gap: 1.5 }}>
                  {inspectionData.items.map((entry, index) => (
                    <CacheEntryLog
                      key={entry.key || `${entry.id}-${index}`}
                      entry={entry}
                      includeValue={appliedInspectionFilters.includeValue}
                    />
                  ))}
                </Box>
              )}

              {inspectionData.nextCursor && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      fetchCacheEntries({
                        filters: appliedInspectionFilters,
                        cursor: inspectionData.nextCursor,
                        append: true,
                      })
                    }
                    disabled={loadingMoreInspection}
                    sx={{ borderColor: "#f97316", color: "#f97316" }}
                  >
                    {loadingMoreInspection ? (
                      <>
                        <CircularProgress size={16} sx={{ color: "#f97316", mr: 1 }} />
                        Loading...
                      </>
                    ) : "Load more"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={tagInvalidationOpen}
        onClose={invalidatingTags ? undefined : () => setTagInvalidationOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            border: "1px solid rgba(220, 38, 38, 0.22)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#202531" }}>
          Invalidate caches by tag?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#4b5563" }}>
            Every cache entry carrying any of these tags will be invalidated for tenant{" "}
            <Box component="span" sx={{ color: "#202531", fontWeight: 700 }}>
              {tenantId}
            </Box>
            .
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, my: 2 }}>
            {appliedTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ ...getTagChipStyle(tag), fontWeight: 700 }}
              />
            ))}
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: "12px",
              color: "#9a3412",
              backgroundColor: "#fff7ed",
              border: "1px solid #fed7aa",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Cache name, entry kind, and key filters do not limit this operation.
            </Typography>
            {appliedTags.length > 1 && appliedInspectionFilters.tagMatch === "All" && (
              <Typography variant="body2" sx={{ mt: 0.75 }}>
                The inspection used “Match all”, but invalidation applies to entries matching any
                listed tag.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setTagInvalidationOpen(false)}
            disabled={invalidatingTags}
            sx={{ color: "#6b7280" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={
              invalidatingTags
                ? <CircularProgress size={16} color="inherit" />
                : <DeleteSweepOutlinedIcon />
            }
            onClick={confirmTagInvalidation}
            disabled={invalidatingTags}
            autoFocus
          >
            {invalidatingTags ? "Invalidating..." : "Invalidate caches"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CacheV2Section;
